import { Chat } from "../../../entity/Chat";
import { VMessage } from "../../../entity/VMessage";
import { Method, ROUTES } from "../../configuration/Configuration";
import { fetchAPI } from "../../configuration/MainService";
import { fetchAPIRealtime } from "../../configuration/MainServiceRealtime";
import { ChatDto } from "../../dto/Chat.Dto";
import { VMessageDto } from "../../dto/VMessage.Dto";
import {
  mapChatDtoToEntity,
  mapMessageDtoToEntity,
} from "../../mapper/ChatMapper";

class WSChat {
  private static instance: WSChat;

  private constructor() {}

  public static getInstance(): WSChat {
    if (!WSChat.instance) {
      WSChat.instance = new WSChat();
    }
    return WSChat.instance;
  }

  getChatByStoreIdRealtime(
    storeId: string,
    onChatChange: (chat: Chat[]) => void,
    onError?: (error: any) => void,
  ) {
    try {
      const url = `${ROUTES.CHATS}${ROUTES.REALTIME_CHATS}${storeId}`;

      const unsubscribe = fetchAPIRealtime<ChatDto[]>({
        url,
        isAuthenticated: true,

        onMessage: (data) => {
          const chats = mapChatDtoToEntity(data);
          onChatChange(chats);
        },

        onError: (error) => {
          console.error("Realtime chats failed:", error);
          onError?.(error);
        },

        onOpen: () => {
          console.log("Realtime chats connected");
        },
      });

      return unsubscribe;
    } catch (error) {
      console.error("Get realtime chats failed:", error);
      throw error;
    }
  }

  getMessageByChatIdRealtime(
    chatId: string,
    onChatChange: (messages: VMessage[]) => void,
    onError?: (error: any) => void,
  ) {
    try {
      const url = `${ROUTES.CHATS}${ROUTES.REALTIME_MESSAGES_BY_CHAT_ID}${chatId}`;

      const unsubscribe = fetchAPIRealtime<VMessageDto[]>({
        url,
        isAuthenticated: true,

        onMessage: (data) => {
          const chats = mapMessageDtoToEntity(data);
          onChatChange(chats);
        },

        onError: (error) => {
          console.error("Realtime messages failed:", error);
          onError?.(error);
        },

        onOpen: () => {
          console.log("Realtime messages connected");
        },
      });

      return unsubscribe;
    } catch (error) {
      console.error("Get realtime messages failed:", error);
      throw error;
    }
  }

  async sendMessage(message: VMessage): Promise<string> {
    try {
      const url = `${ROUTES.CHATS}${ROUTES.SEND_MESSAGE}`;
      const res = await fetchAPI<string>({
        url,
        method: Method.POST,
        params: message,
        isAuthenticated: true,
      });
      return res.data;
    } catch (error) {
      console.error("Send message failed:", error);
      throw error;
    }
  }

  async getAllChats(): Promise<Chat[]> {
    try {
      const url = `${ROUTES.CHATS}${ROUTES.GET_ALL_CHATS}`;
      const res = await fetchAPI<ChatDto[]>({
        url,
        method: Method.GET,
        isAuthenticated: true,
      });
      return mapChatDtoToEntity(res.data);
    } catch (error) {
      console.error("Get all chats failed:", error);
      throw error;
    }
  }

  async createChat(chat: { storeId: string; userId: string }): Promise<string> {
    try {
      const url = `${ROUTES.CHATS}${ROUTES.CREATE_CHAT}`;
      const res = await fetchAPI<string>({
        url,
        method: Method.POST,
        params: chat,
        isAuthenticated: true,
      });
      return res.data;
    } catch (error) {
      console.error("Create chat failed:", error);
      throw error;
    }
  }

  async updateChatStatus(chatId: string, status: string): Promise<void> {
    try {
      const url = `${ROUTES.CHATS}${ROUTES.UPDATE_CHAT_STATUS}`;
      await fetchAPI({
        url,
        method: Method.PUT,
        params: { chatId, status },
        isAuthenticated: true,
      });
    } catch (error) {
      console.error("Update chat status failed:", error);
      throw error;
    }
  }

  async updateChatTitle(chatId: string, title: string): Promise<void> {
    try {
      const url = `${ROUTES.CHATS}${ROUTES.UPDATE_CHAT_TITLE}`;
      await fetchAPI({
        url,
        method: Method.PUT,
        params: { chatId, title },
        isAuthenticated: true,
      });
    } catch (error) {
      console.error("Update chat title failed:", error);
      throw error;
    }
  }
}

export default WSChat.getInstance();
